// Turning server rows into packets — the pure half of lamp control.
//
// A lamp node (from core/api/bleNodes.js) carries one radio identity: Codec,
// ControllerId, LampIndex, Seed, TxCount. This module builds commands against
// that identity and puts them on the air; it holds no state and touches no
// REST, so it is tested with the radio mocked. The hook in
// core/hooks/useBleNode.js does the state, the counters and the audit log.
//
// ONE IDENTITY PER LAMP — verified on hardware 2026-09-15: a lamp keeps only
// the first identity it hears in a pairing window. So there is no shared
// "group identity"; a command to a group or parent is the same packet to each
// lamp beneath it in turn (sendToEach), each held on the air for HoldMs.
//
// ⚠️ WHY THE LOOP TAKES SECONDS, NOT MILLISECONDS. The lamp is not listening
// continuously — it wakes periodically to check for advertisements — so each
// packet must be HELD long enough to be caught, and the phone's radio holds
// one advertisement at a time. ~300 ms per lamp is reliable; 25 lamps ≈ 7.5 s.
// HoldMs is a per-node setting so a batch that catches faster can be tuned.
//
// ⚠️ PAIRING IS UNADDRESSED: every lamp in pairing mode within range adopts
// the identity. All other lamps must be off at the wall while one is paired.

import { broadcast } from './advertiser';
import { parseLampState, stateForValues } from '../lampState';
import {
  CMD, cwwChannels, getCodec, nextTxCount,
} from './codecs';

export const DURATION_COMMAND = 400;
export const DURATION_PAIR = 3000;
/** Default per-lamp hold inside a loop; the node's HoldMs overrides it. */
export const DURATION_EACH = 300;
/** Each step of the identify blink. */
export const DURATION_BLINK = 350;
/**
 * The OFF step of a blink is held longer than the ON step — seen on hardware
 * 2026-10-11: with equal short steps the lamp's own fade swallowed the dark
 * moment and the blink was not recognisable.
 */
export const DURATION_BLINK_OFF = 900;
/** Extra dark time between the off and on passes of a group blink. */
export const BLINK_DARK_PAUSE_MS = 600;

/** A row is sendable only when the server handed us its identity. */
export function canSend(row) {
  return !!row && Number(row.CanSend) === 1 && row.ControllerId != null && row.Seed != null;
}

/** Server row -> LampConfig for the codec. Throws on a locked / withheld row. */
export function identityOf(row) {
  if (!canSend(row)) {
    const e = new Error(Number(row?.Locked) === 1 || Number(row?.CanSend) === 0
      ? 'This lamp is locked by its owner.'
      : 'No radio identity for this lamp.');
    e.code = 'LOCKED';
    throw e;
  }
  return {
    codec: String(row.Codec),
    // eslint-disable-next-line no-bitwise
    id: Number(row.ControllerId) >>> 0,
    index: Number(row.LampIndex) || 0,
    seed: Number(row.Seed),
    txCount: Number(row.TxCount) || 0,
  };
}

/**
 * "white,warm" as the Wi-Fi WWH light speaks it -> the codec's cww command.
 * Cold channel = white, warm channel = warm white; both 0..255.
 */
export function cwwFromChannels(codecId, white, warm) {
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(Number(v) || 0)));
  return getCodec(codecId).cww(clamp(white), clamp(warm));
}

/** Brightness 0..1 + warmth 0..1 -> cww, for a UI that prefers those axes. */
export function cwwFromBrightness(codecId, brightness, warmth) {
  const { cold, warm } = cwwChannels(brightness, warmth);
  return getCodec(codecId).cww(cold, warm);
}

export const COMMANDS = {
  on: { cmd: CMD.on },
  off: { cmd: CMD.off },
  night: { cmd: CMD.nightMode },
  pair: { cmd: CMD.pair },
  unpair: { cmd: CMD.unpair },
};

/**
 * Encode one command against one identity and hold it on the air.
 * Returns the advanced txCount the caller must persist.
 */
export async function transmit(row, cmd, { durationMs = DURATION_COMMAND, includeFlags = true } = {}) {
  const conf = identityOf(row);
  const advanced = { ...conf, txCount: nextTxCount(conf.txCount) };
  await broadcast(getCodec(advanced.codec).encode(cmd, advanced), durationMs, includeFlags);
  return advanced.txCount;
}

/** Hold `pair` on the lamp's identity for the whole pairing window. */
export async function pairLamp(lamp, { includeFlags = true } = {}) {
  return transmit(lamp, COMMANDS.pair, { durationMs: DURATION_PAIR, includeFlags });
}

/** Tell the lamp to forget this identity. */
export async function unpairLamp(lamp, { includeFlags = true } = {}) {
  return transmit(lamp, COMMANDS.unpair, { durationMs: DURATION_PAIR, includeFlags });
}

/**
 * The same command to every sendable lamp in turn. Skips locked rows.
 * `onProgress(i, n, lamp)` before each send. Returns [{ NodeId, TxCount }].
 */
export async function sendToEach(lamps, cmd, { holdMs = DURATION_EACH, includeFlags = true, onProgress } = {}) {
  const out = [];
  for (let i = 0; i < lamps.length; i += 1) {
    const lamp = lamps[i];
    if (!canSend(lamp)) continue; // eslint-disable-line no-continue
    onProgress?.(i, lamps.length, lamp);
    // eslint-disable-next-line no-await-in-loop
    const tx = await transmit(lamp, cmd, { durationMs: holdMs, includeFlags });
    out.push({ NodeId: lamp.NodeId, TxCount: tx });
  }
  return out;
}

/** Blinks per identify — a fixed count, so "which one is it" never depends on luck. */
export const BLINKS = 5;

/** Was this lamp on, by its last recorded state? Never recorded -> treated as on. */
const wasOnOf = (row) => parseLampState(row?.LastValues)?.on ?? true;

/**
 * Blink one lamp so the user can see WHICH fixture a row is: BLINKS times,
 * ENDING IN THE STATE IT WAS IN — a lit lamp goes off·on ×5, a dark one on·off
 * ×5. Ten packets, ~3.5 s. Returns the advanced counter.
 */
export async function identify(row, { includeFlags = true, times = BLINKS } = {}) {
  const pair = wasOnOf(row) ? [COMMANDS.off, COMMANDS.on] : [COMMANDS.on, COMMANDS.off];
  let cur = row;
  for (let i = 0; i < times; i += 1) {
    for (const cmd of pair) {
      // eslint-disable-next-line no-await-in-loop
      const tx = await transmit(cur, cmd, { durationMs: cmd === COMMANDS.off ? DURATION_BLINK_OFF : DURATION_BLINK, includeFlags });
      cur = { ...cur, TxCount: tx };
    }
  }
  return cur.TxCount;
}

/**
 * Blink many lamps: off to each, then on to each, BLINKS times; then the lamps
 * that were off are switched off again, so every lamp ends as it was.
 * Returns [{ NodeId, TxCount }].
 */
export async function identifyEach(lamps, opts = {}) {
  const times = opts.times ?? BLINKS;
  let cur = lamps;
  const advance = (sent) => {
    const byId = new Map(sent.map((o) => [o.NodeId, o.TxCount]));
    cur = cur.map((l) => (byId.has(l.NodeId) ? { ...l, TxCount: byId.get(l.NodeId) } : l));
  };
  for (let i = 0; i < times; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    advance(await sendToEach(cur, COMMANDS.off, opts));
    // Keep the group dark long enough to SEE it (seen 2026-10-11: a quick
    // off·on did not register as a blink).
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => { setTimeout(r, BLINK_DARK_PAUSE_MS); });
    // eslint-disable-next-line no-await-in-loop
    advance(await sendToEach(cur, COMMANDS.on, opts));
  }
  const wereOff = new Set(lamps.filter((l) => !wasOnOf(l)).map((l) => l.NodeId));
  if (wereOff.size) advance(await sendToEach(cur.filter((l) => wereOff.has(l.NodeId)), COMMANDS.off, opts));
  return cur.filter(canSend).map((l) => ({ NodeId: l.NodeId, TxCount: l.TxCount }));
}

/**
 * The packets a "set channels" action needs for ONE lamp. A lamp that is off
 * does not visibly react to a bare cww on every firmware, so when its previous
 * values were dark an ON goes first; both channels at zero is simply OFF.
 * -> [{ cmd, name }] in send order.
 */
export function planChannelSend(codecId, white, warm, previousValues, swapped = false) {
  const w = Number(white) || 0;
  const ww = Number(warm) || 0;
  if (w === 0 && ww === 0) return [{ name: 'off', cmd: COMMANDS.off }];
  // Dark = never recorded, both channels zero, OR switched off (values kept, p = 0).
  const prev = parseLampState(previousValues);
  const wasDark = !prev || !prev.on;
  const steps = [];
  if (wasDark) steps.push({ name: 'on', cmd: COMMANDS.on });
  // A lamp with reversed LEDs gets the two channels the other way round.
  steps.push({ name: 'cww', cmd: swapped ? cwwFromChannels(codecId, ww, w) : cwwFromChannels(codecId, w, ww) });
  return steps;
}

/**
 * Set channels on many lamps: each lamp gets its own plan (an ON first only if
 * THAT lamp was dark). Returns [{ NodeId, TxCount, LastValues }].
 */
export async function setChannelsEach(lamps, white, warm, { holdMs = DURATION_EACH, includeFlags = true, onProgress } = {}) {
  const payload = stateForValues(white, warm);
  const out = [];
  for (let i = 0; i < lamps.length; i += 1) {
    const lamp = lamps[i];
    if (!canSend(lamp)) continue; // eslint-disable-line no-continue
    onProgress?.(i, lamps.length, lamp);
    let tx = Number(lamp.TxCount) || 0;
    for (const step of planChannelSend(lamp.Codec, white, warm, lamp.LastValues, Number(lamp.SwapWarm) === 1)) {
      // eslint-disable-next-line no-await-in-loop
      tx = await transmit({ ...lamp, TxCount: tx }, step.cmd, { durationMs: holdMs, includeFlags });
    }
    out.push({ NodeId: lamp.NodeId, TxCount: tx, LastValues: payload });
  }
  return out;
}

/** "255,128" -> [255, 128]; anything unusable -> null. */
export function parseValues(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const parts = s.split(',').map((x) => parseInt(x.trim(), 10));
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null;
  return [Math.max(0, Math.min(255, parts[0])), Math.max(0, Math.min(255, parts[1]))];
}

export const formatValues = (white, warm) => `${Math.round(white)},${Math.round(warm)}`;
